import type { DashboardConfig } from "./types";

interface ValidationResult {
  valid: boolean;
  errors: string[];
  config?: DashboardConfig;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asString(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  return typeof value === "string" ? value.trim() : "";
}

function asPositiveInteger(record: Record<string, unknown>, key: string, fallback: number): number {
  const value = record[key];
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return Math.round(value);
  }
  return fallback;
}

function asOptionalHttpsUrl(record: Record<string, unknown>, key: string): string | undefined {
  const rawValue = asString(record, key);
  if (!rawValue) {
    return undefined;
  }
  try {
    const parsed = new URL(rawValue);
    return parsed.protocol === "https:" ? parsed.toString() : undefined;
  } catch {
    return undefined;
  }
}

export function validateConfig(value: unknown): ValidationResult {
  const errors: string[] = [];
  if (!isRecord(value)) {
    return { valid: false, errors: ["Configuration must be a JSON object."] };
  }

  const services = value.services;
  if (!isRecord(services)) {
    errors.push("services must be a JSON object.");
  }

  const config: DashboardConfig = {
    portalUrl: asString(value, "portalUrl"),
    oauthAppId: asString(value, "oauthAppId"),
    webMapId: asString(value, "webMapId"),
    partnerIntakeUrl: asOptionalHttpsUrl(value, "partnerIntakeUrl"),
    services: {
      metricsUrl: isRecord(services) ? asString(services, "metricsUrl") : "",
      vesselPositionsUrl: isRecord(services) ? asString(services, "vesselPositionsUrl") : "",
      portInfrastructureUrl: isRecord(services) ? asString(services, "portInfrastructureUrl") : ""
    },
    refreshSeconds: asPositiveInteger(value, "refreshSeconds", 180)
  };

  if (!config.portalUrl) {
    errors.push("portalUrl is required.");
  }
  if (!config.oauthAppId) {
    errors.push("oauthAppId is required.");
  }
  if (!config.webMapId) {
    errors.push("webMapId is required.");
  }
  if (!config.services.metricsUrl) {
    errors.push("services.metricsUrl is required.");
  }
  if (!config.services.vesselPositionsUrl) {
    errors.push("services.vesselPositionsUrl is required.");
  }
  if (!config.services.portInfrastructureUrl) {
    errors.push("services.portInfrastructureUrl is required.");
  }
  if (asString(value, "partnerIntakeUrl") && config.partnerIntakeUrl === undefined) {
    errors.push("partnerIntakeUrl must be an HTTPS URL when provided.");
  }

  return {
    valid: errors.length === 0,
    errors,
    config: errors.length === 0 ? config : undefined
  };
}

async function fetchJson(path: string): Promise<unknown | null> {
  const response = await fetch(path, { cache: "no-store" });
  if (!response.ok) {
    return null;
  }
  const text = await response.text();
  const trimmed = text.trim();
  if (trimmed.startsWith("<!doctype") || trimmed.startsWith("<html")) {
    return null;
  }
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    throw new Error(`${path} is not valid JSON.`);
  }
}

export function appRootPath(): string {
  const path = window.location.pathname;
  const normalizedPath = path.endsWith("/") ? path : `${path}/`;
  const lowerPath = normalizedPath.toLowerCase();
  for (const sectionPath of ["/dashboard/", "/metrics/", "/data-sources/", "/whats-next/"]) {
    const sectionIndex = lowerPath.lastIndexOf(sectionPath);
    if (sectionIndex >= 0) {
      return normalizedPath.slice(0, sectionIndex + 1);
    }
  }
  return new URL(".", window.location.href).pathname;
}

export async function loadConfig(): Promise<DashboardConfig> {
  const appRoot = appRootPath();
  const payload = (await fetchJson(`${appRoot}config.json`)) ?? (await fetchJson(`${appRoot}config.example.json`));
  const result = validateConfig(payload);
  if (!result.valid || result.config === undefined) {
    throw new Error(`Dashboard configuration is incomplete: ${result.errors.join(" ")}`);
  }
  return result.config;
}

export async function loadPartnerIntakeUrl(): Promise<string | undefined> {
  const appRoot = appRootPath();
  const payload = (await fetchJson(`${appRoot}config.json`)) ?? (await fetchJson(`${appRoot}config.example.json`));
  if (!isRecord(payload)) {
    return undefined;
  }
  return asOptionalHttpsUrl(payload, "partnerIntakeUrl");
}
